import { useState, useEffect } from "react";
import { Grid } from "@mui/material";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
} from "@mui/x-data-grid";
import { Button, Box } from "@mui/material";
import { useTheme } from "@emotion/react";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";

// Components
import { useNotification } from "../utils/Notification";
import { trackEvent } from "../utils/Utils";
import AiDatabase from "../modals/AiDatabase";
import Plans from "../modals/Plans";
import Header from "./Header";

// Theme
import { tokens } from "../../theme";

// Supabase
import supabase from "../../supabaseClient";

// Context
import { useAuth } from "../../contexts/AuthContext";

export default function Ai() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [databases, setDatabases] = useState([]);
  const [faq, setFaq] = useState([]);
  const [groups, setGroups] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDatabase, setSelectedDatabase] = useState(null);
  const [plansModalOpen, setPlansModalOpen] = useState(false);
  const [planData, setPlanData] = useState([]);
  const { t } = useTranslation();

  // Context
  const { showNotification } = useNotification();

  const columns = [
    { field: "name", headerName: t("ai.name"), width: 300 },
    {
      field: "description",
      headerName: t("ai.description"),
      width: 600,
    },
  ];

  useEffect(() => {
    const fetchDatabases = async () => {
      const { data, error } = await supabase.from("aidatabase").select(
        `
            id,
            name,
            groups_ids,
            description,
            applies_to_unsaved_contacts,
            applies_to_saved_contacts
          `
      );

      if (error) {
        showNotification(t("ai.fetchError", { error: error.message }));
      } else {
        setDatabases(data);
      }
    };

    const fetchFaq = async () => {
      const { data, error } = await supabase.from("aifaq").select(
        `
          id,
          team_id,
          aidatabase_id,
          question,
          answer
        `
      );

      if (error) {
        showNotification(t("ai.fetchError", { error: error.message }));
      } else {
        setFaq(data);
      }
    };

    const fetchGroups = async () => {
      const { data, error } = await supabase.from("groups").select(
        `
            id,
            name
          `
      );
      if (error) {
        showNotification(t("ai.fetchError", { error: error.message }));
      } else {
        setGroups(data);
      }
    };

    const fetchPlan = async () => {
      const { data, error } = await supabase
        .from("teams")
        .select("plan(ai)")
        .single();

      if (!error) {
        setPlanData(data.plan);
      }
    };

    fetchDatabases();
    fetchFaq();
    fetchGroups();
    fetchPlan();
  }, [modalOpen, plansModalOpen]);

  const handleNewDatabase = () => {
    if (!planData.ai) {
      showNotification(t("ai.upgradePlan"), "warning");
      setPlansModalOpen(true);
      return;
    }
    trackEvent("ai-new_database_clicked");
    setSelectedDatabase({ name: "", description: "", groups_ids: [] });
    setModalOpen(true);
  };

  const handleRowClick = (params) => {
    trackEvent("ai-database_row_clicked");
    setSelectedDatabase(params.row);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    trackEvent("ai-database_modal_closed");
    setModalOpen(false);
    setSelectedDatabase(null);
  };

  const handleClosePlansModal = () => {
    trackEvent("ai-plan_modal_closed");
    setPlansModalOpen(false);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: "1600px", mx: "auto" }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Header title={t("ai.title")} subtitle={t("ai.subtitle")} />
      </Box>

      {/* Grid Container for Video*/}
      <Grid container spacing={4} mb={4}>
        <Grid item xs={12} md={6} lg={4}>
          <Box
            sx={{
              width: "100%",
              height: 220,
              overflow: "hidden",
              borderRadius: "16px",
              boxShadow: theme.palette.mode === 'dark' ? "0 8px 32px 0 rgba(0, 0, 0, 0.3)" : "0 8px 32px 0 rgba(31, 38, 135, 0.05)",
              border: theme.palette.mode === 'dark' ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid rgba(0, 0, 0, 0.05)",
            }}
          >
            <iframe
              src={`https://www.youtube.com/embed/${t("tutorials.aiUrl")}`}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title="YouTube video player"
              style={{ width: "100%", height: "100%" }}
            ></iframe>
          </Box>
        </Grid>
      </Grid>
      {/* End of Grid Container */}

      {/* Content wrapper with glassmorphism */}
      <Box
        sx={{
          bgcolor: theme.palette.mode === 'dark' ? "rgba(20, 27, 45, 0.7)" : "#ffffff",
          backdropFilter: "blur(10px)",
          borderRadius: "16px",
          boxShadow: theme.palette.mode === 'dark' ? "0 8px 32px 0 rgba(0, 0, 0, 0.3)" : "0 8px 32px 0 rgba(31, 38, 135, 0.05)",
          border: theme.palette.mode === 'dark' ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid rgba(0, 0, 0, 0.05)",
          p: { xs: 2, md: 3 },
          minHeight: "500px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            height: "auto",
            minHeight: "400px",
            width: "100%",
            "& .MuiDataGrid-root": {
              border: "none",
              bgcolor: "transparent",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: theme.palette.mode === 'dark' ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid rgba(0, 0, 0, 0.05)",
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: theme.palette.mode === 'dark' ? "rgba(0,0,0,0.2)" : "#f8f9fa",
              borderBottom: "none",
              color: theme.palette.mode === 'dark' ? "#a1a4ab" : "#141b2d",
              fontWeight: "bold",
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: "transparent",
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "none",
              backgroundColor: theme.palette.mode === 'dark' ? "rgba(0,0,0,0.2)" : "#f8f9fa",
              borderBottomLeftRadius: "16px",
              borderBottomRightRadius: "16px",
            },
            "& .MuiCheckbox-root": {
              color: `${theme.palette.mode === 'dark' ? "#4cceac" : "#3da58a"} !important`,
            },
            "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
              color: `${theme.palette.mode === 'dark' ? "#a1a4ab" : "#727681"} !important`,
              fontWeight: "bold",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: theme.palette.mode === 'dark' ? "rgba(255, 255, 255, 0.02)" : "rgba(0, 0, 0, 0.02)",
            },
          }}
        >
          <DataGrid
            rows={databases}
            columns={columns}
            onRowClick={handleRowClick}
            disableSelectionOnClick
            autoHeight
            sx={{ borderRadius: "16px", overflow: "hidden" }}
            components={{
              Toolbar: () => (
                <GridToolbarContainer sx={{ p: 2, borderBottom: theme.palette.mode === 'dark' ? "1px solid rgba(255,255,255,0.05)" : "1px solid rgba(0,0,0,0.05)", gap: 1 }}>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleNewDatabase}
                    sx={{
                      borderRadius: "8px",
                      fontWeight: "bold",
                      background: "linear-gradient(90deg, #7949E7 0%, #9A67ED 100%)",
                      color: "#fff",
                      boxShadow: "0 4px 14px 0 rgba(121, 73, 231, 0.39)",
                      "&:hover": { background: "linear-gradient(90deg, #6B53BB 0%, #7949E7 100%)" }
                    }}
                  >
                    {t("ai.newDatabase")}
                  </Button>

                  <Box sx={{ flexGrow: 1 }} />

                  <GridToolbarFilterButton />
                  <GridToolbarColumnsButton />
                  <GridToolbarDensitySelector />
                </GridToolbarContainer>
              ),
            }}
          />
        </Box>
      </Box>

      <AiDatabase
        open={modalOpen}
        onClose={handleCloseModal}
        database={selectedDatabase}
        faq={faq}
        groups={groups}
      />

      <Plans open={plansModalOpen} onClose={handleClosePlansModal} />
    </Box>
  );
}
